import { StyleSheet, Platform } from 'react-native'
import type { ViewStyle } from 'react-native'
import type { Theme } from '../src/theme/getTheme'

export function getAccountAdminPageStyles(theme: Theme) {
  return StyleSheet.create({
    container: {
      flex: 1,
      backgroundColor: theme.background.primary,
      ...(Platform.OS === 'web' && {
        // Flex min-height:auto otherwise sizes to ScrollView content and inner scroll never activates
        minHeight: 0 as never,
      }),
    },
    siteMarketingRootBg: {
      backgroundColor: theme.background.primary,
    } as ViewStyle,
    content: {
      flex: 1,
      paddingHorizontal: theme.spacing.huge,
      paddingTop: theme.spacing.xl,
      paddingBottom: theme.spacing.huge,
      ...(Platform.OS === 'web' && {
        minHeight: 0 as never,
      }),
    },
    siteMarketingContent: {
      paddingHorizontal: 0,
      paddingTop: 0,
      paddingBottom: 0,
      position: 'relative',
      overflow: 'hidden',
      backgroundColor: theme.background.primary,
    },
    siteGlow: {
      position: 'absolute',
      borderRadius: 999,
      opacity: 0.78,
    },
    siteGlowPrimary: {
      top: -190,
      right: -150,
      width: 520,
      height: 520,
      backgroundColor: theme.primary + '33',
    },
    siteGlowGold: {
      bottom: -210,
      left: -130,
      width: 440,
      height: 440,
      backgroundColor: theme.accent + '2A',
    },
    siteCenteredContent: {
      flex: 1,
      width: '100%',
      paddingHorizontal: 24,
      paddingTop: 42,
      paddingBottom: 56,
      alignItems: 'center',
      ...(Platform.OS === 'web' && {
        minHeight: 0 as never,
      }),
    },
    siteCenteredScrollContent: {
      width: '100%',
      paddingHorizontal: 24,
      paddingTop: 42,
      paddingBottom: 72,
      alignItems: 'center',
    },
    siteEmptyCard: {
      width: '100%',
      maxWidth: 860,
      alignSelf: 'center',
      borderRadius: 30,
      borderWidth: 1,
      borderColor: theme.gray[300],
      backgroundColor: theme.background.card,
      paddingVertical: 34,
      paddingHorizontal: 34,
      shadowColor: theme.primaryDark,
      shadowOffset: { width: 0, height: 24 },
      shadowOpacity: 0.13,
      shadowRadius: 42,
      elevation: 10,
    },
    /** Site org admin: one column, max width. */
    siteContentColumn: {
      flex: 1,
      width: '100%',
      maxWidth: 960,
      alignSelf: 'center',
      ...(Platform.OS === 'web' && {
        minHeight: 0 as never,
      }),
    },
    /**
     * Site org admin on web: main column lives inside a single outer ScrollView (see AccountAdminScreen).
     * No flex:1 — flex growth here breaks scroll content measurement on RN Web.
     */
    siteContentColumnInMainScroll: {
      width: '100%',
      maxWidth: 960,
      alignSelf: 'center',
      borderRadius: 30,
      borderWidth: 1,
      borderColor: theme.gray[300],
      backgroundColor: theme.background.card,
      paddingVertical: 30,
      paddingHorizontal: 34,
      shadowColor: theme.primaryDark,
      shadowOffset: { width: 0, height: 24 },
      shadowOpacity: 0.13,
      shadowRadius: 42,
      elevation: 10,
    },
    /**
     * Web >= ACCOUNT_ADMIN_SIDEBAR_BREAKPOINT_PX: flex-row container for sidebar + content.
     * Sits inside siteContentColumnInMainScroll (which already has max-width 960).
     */
    siteWebSidebarRow: {
      flexDirection: 'row',
      flex: 1,
      alignItems: 'flex-start',
    } as ViewStyle,
    /** The content area to the right of the sidebar — flex 1, min-width 0 to allow text wrapping. */
    siteWebSidebarContent: {
      flex: 1,
      ...(Platform.OS === 'web' && {
        minWidth: 0 as never,
      }),
      paddingHorizontal: 24,
      paddingTop: 4,
      paddingBottom: 32,
    } as ViewStyle,
    /** Outer scroll for `/account-admin` (web) — one scroll container for title + tab body. */
    siteWebMainScroll: {
      flex: 1,
      ...(Platform.OS === 'web' && {
        minHeight: 0 as never,
      }),
    },
    siteWebMainScrollContent: {
      width: '100%',
      paddingBottom: theme.spacing.huge * 2,
      paddingHorizontal: 24,
      paddingTop: 42,
      ...(Platform.OS === 'web' && {
        alignSelf: 'stretch' as never,
      }),
    },
    contentInnerInApp: {
      flex: 1,
      width: '100%',
      ...(Platform.OS === 'web' && {
        minHeight: 0 as never,
      }),
    },
    orgToolbar: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 12,
      marginBottom: 16,
      width: '100%',
      maxWidth: 720,
      alignSelf: 'flex-start',
    },
    orgToolbarMenuPressable: {
      paddingVertical: 10,
      paddingHorizontal: 10,
      borderRadius: 10,
      backgroundColor: theme.background.card,
      borderWidth: 1,
      borderColor: theme.gray[300],
    },
    orgToolbarMenuPressed: {
      opacity: 0.88,
    },
    orgToolbarMenuHover: {
      opacity: 0.94,
    },
    orgToolbarTitle: {
      fontSize: 18,
      fontWeight: '800',
      color: theme.gray[900],
      flex: 1,
    },
    /** Site shell: section title under marketing nav (org nav in {@link LandingOrgAdminNewNav}). */
    orgSiteSectionTitle: {
      fontSize: 22,
      fontWeight: '800',
      color: theme.gray[900],
      marginBottom: 16,
      width: '100%',
      textAlign: 'left',
    },
    /** Hairline under Branding / Members / Settings title (site + in-app toolbar). */
    orgTabDivider: {
      height: StyleSheet.hairlineWidth,
      width: '100%',
      maxWidth: 720,
      alignSelf: 'flex-start',
      backgroundColor: theme.gray[200],
      marginBottom: theme.spacing.lg,
    },
    /** B2B Stripe success return: sync session + refresh in flight. */
    stripeReturnSyncingStrip: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: theme.spacing.md,
      marginBottom: theme.spacing.lg,
      paddingVertical: theme.spacing.md,
      paddingHorizontal: theme.spacing.lg,
      width: '100%',
      maxWidth: 720,
      alignSelf: 'stretch',
      backgroundColor: theme.gray[100],
      borderRadius: theme.borderRadius.md,
      borderWidth: 1,
      borderColor: theme.gray[200],
    },
    stripeReturnSyncingText: {
      flex: 1,
      fontSize: 15,
      fontWeight: '600',
      color: theme.gray[800],
    },
    /**
     * Mobile web (site surface, < ACCOUNT_ADMIN_SIDEBAR_BREAKPOINT_PX):
     * Horizontal scrollable tab strip at the top of the card.
     * Negative horizontal margin breaks out of the card's paddingHorizontal (34px) so the
     * hairline separator runs edge-to-edge.
     */
    mobileTabStrip: {
      marginHorizontal: -34,
      borderBottomWidth: StyleSheet.hairlineWidth,
      borderBottomColor: theme.gray[200],
      marginBottom: theme.spacing.lg,
    },
    mobileTabStripContent: {
      flexDirection: 'row',
    },
    mobileTabItem: {
      flex: 1,
      flexDirection: 'column',
      alignItems: 'center',
      gap: 3,
      paddingVertical: 8,
      paddingHorizontal: 4,
      borderBottomWidth: 2,
      borderBottomColor: 'transparent',
    },
    mobileTabItemActive: {
      borderBottomColor: theme.primaryLight,
    } as ViewStyle,
    mobileTabLabel: {
      fontSize: 11,
      fontWeight: '600',
      color: theme.gray[500],
    },
    mobileTabLabelActive: {
      color: theme.primaryLight,
    },
    mobileTabItemLocked: {
      opacity: 0.55,
    },
    mobileTabLabelLocked: {
      color: theme.gray[400],
    },
    lockedTabPanel: {
      flex: 1,
      alignItems: 'center',
      justifyContent: 'center',
      padding: 32,
      gap: theme.spacing.lg,
    },
    lockedTabTitle: {
      fontSize: 18,
      fontWeight: '700',
      color: theme.gray[700],
      textAlign: 'center',
    },
    lockedTabBody: {
      fontSize: 15,
      color: theme.gray[500],
      textAlign: 'center',
      lineHeight: 22,
    },
    lockedTabBtn: {
      marginTop: theme.spacing.sm,
      paddingHorizontal: theme.spacing.xxl,
      paddingVertical: theme.spacing.md,
      backgroundColor: theme.primary,
      borderRadius: theme.borderRadius.md,
    },
    lockedTabBtnPressed: {
      opacity: 0.82,
    },
    lockedTabBtnHover: {
      opacity: 0.9,
    },
    lockedTabBtnText: {
      fontSize: 15,
      fontWeight: '700',
      color: '#fff',
    },
    center: {
      flex: 1,
      justifyContent: 'center',
      alignItems: 'center',
      padding: 24,
    },
    headline: {
      fontSize: 20,
      fontWeight: '800',
      color: theme.gray[900],
      marginBottom: 8,
      textAlign: 'center',
    },
    sub: {
      fontSize: 15,
      color: theme.gray[600],
      textAlign: 'center',
      marginBottom: 20,
    },
    input: {
      borderWidth: 1,
      borderColor: theme.gray[300],
      borderRadius: 10,
      paddingHorizontal: 14,
      paddingVertical: 12,
      fontSize: 16,
      color: theme.gray[900],
      backgroundColor: theme.background.card,
      marginBottom: 12,
      width: '100%',
      maxWidth: 400,
      alignSelf: 'center',
    },
    rowActions: {
      flexDirection: 'row',
      gap: 12,
      flexWrap: 'wrap',
      justifyContent: 'center',
    },
    primaryBtn: {
      backgroundColor: theme.primary,
      paddingVertical: 12,
      paddingHorizontal: 20,
      borderRadius: 10,
    },
    primaryBtnPressed: {
      opacity: 0.9,
    },
    primaryBtnText: {
      color: theme.white,
      fontWeight: '700',
    },
    outlineBtn: {
      marginTop: theme.spacing.md,
      borderWidth: 2,
      borderColor: theme.primary,
      paddingVertical: 12,
      paddingHorizontal: 20,
      borderRadius: 10,
      alignSelf: 'center',
    },
    outlineBtnPressed: {
      opacity: 0.88,
    },
    outlineBtnHover: {
      opacity: 0.94,
    },
    outlineBtnDisabled: {
      opacity: 0.45,
    },
    outlineBtnText: {
      color: theme.primary,
      fontWeight: '700',
      textAlign: 'center',
    },
    errorText: {
      color: theme.error,
      marginTop: 8,
      textAlign: 'center',
    },
  })
}

export const accountAdminSiteShellStyles = StyleSheet.create({
  /**
   * Site routes (account-admin, packs, org marketing): nav + scrollable body.
   * Web: pin shell to the dynamic viewport and clip overflow so nested ScrollViews get a bounded
   * height (flex + minHeight 0) and scroll instead of growing past the viewport and clipping.
   */
  shell: {
    flex: 1,
    ...(Platform.OS === 'web' && {
      flexDirection: 'column' as never,
      minHeight: '100dvh' as never,
      height: '100dvh' as never,
      maxHeight: '100dvh' as never,
      overflow: 'hidden' as never,
    }),
  },
})

/** “Back to site” — must follow active theme (marketing web is dark-locked). */
export function getAccountAdminBackToSiteChipStyles(theme: Theme) {
  return StyleSheet.create({
    /** Wide web: small chip over the shell (sidebar is the main nav). */
    backFloating: {
      position: 'absolute',
      top: 82,
      left: 16,
      zIndex: 80,
      paddingVertical: 6,
      paddingHorizontal: 12,
      borderRadius: 10,
      backgroundColor: theme.background.primary,
      borderWidth: 1,
      borderColor: theme.gray[300],
      shadowColor: theme.primary,
      shadowOffset: { width: 0, height: 2 },
      shadowOpacity: 0.2,
      shadowRadius: 8,
      elevation: 4,
    },
    backFloatingHovered: {
      borderColor: theme.primaryLight,
    },
    /** Narrow web: full-width bar under the marketing header (no overlapping float). */
    backInlineWrap: {
      width: '100%',
      zIndex: 70,
      borderBottomWidth: 1,
      borderBottomColor: theme.gray[300],
      backgroundColor: theme.background.primary,
      ...(Platform.OS === 'web' && {
        alignSelf: 'stretch' as never,
      }),
    },
    backInlinePressable: {
      flexDirection: 'row',
      alignItems: 'center',
      minHeight: 48,
      paddingVertical: 12,
      paddingHorizontal: 16,
      gap: 8,
      ...(Platform.OS === 'web' && {
        cursor: 'pointer' as never,
      }),
    },
    backInlinePressableHovered: {
      backgroundColor: theme.background.card,
    },
    backFloatingText: {
      fontFamily: Platform.OS === 'web' ? '"Inter", system-ui, sans-serif' : undefined,
      fontSize: 14,
      fontWeight: '600',
      color: theme.primaryLight,
    },
  })
}
